import { t } from "@lingui/macro";
import type { ErrorMessage } from "@reactive-resume/utils";
import { deepSearchAndParseDates } from "@reactive-resume/utils";
import _axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";

import { refreshToken } from "@/client/services/auth";
import { isPaywallCode, openPaywall } from "@/client/stores/paywall";

import { USER_KEY } from "../constants/query-keys";
import { toast } from "../hooks/use-toast";
import { translateError } from "../services/errors/translate-error";
import { isDefinitiveAuthFailure } from "./auth-failure";
import { queryClient } from "./query-client";

declare module "axios" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions, @typescript-eslint/no-explicit-any, unused-imports/no-unused-vars -- module augmentation must be an interface with the same type parameter
  interface AxiosRequestConfig<D = any> {
    /**
     * The caller renders its own upgrade surface for this request, so the
     * interceptor must not also open the global PaywallDialog (two paywalls,
     * two `paywall_viewed` events).
     */
    suppressPaywall?: boolean;
    /**
     * The caller renders this request's failure inline (its own error banner), so
     * the interceptor must not also raise the global "Oops" toast: one failure,
     * one message. The rejected error keeps its `code` and `response` either way.
     */
    suppressErrorToast?: boolean;
  }
}

export const axios = _axios.create({ baseURL: "/api", withCredentials: true });

// Intercept responses to transform ISO dates to JS date objects
axios.interceptors.response.use(
  (response) => {
    const transformedResponse = deepSearchAndParseDates(response.data, [
      "blockedAt",
      "completedAt",
      "appliedAt",
      "createdAt",
      "discoveredAt",
      "emailDate",
      "lastUsedAt",
      "lastValidatedAt",
      "lastViewedAt",
      "lastSyncedAt",
      "postedAt",
      "processedAt",
      "readAt",
      "respondedAt",
      "startedAt",
      "updatedAt",
    ]);
    return { ...response, data: transformedResponse };
  },
  (error) => {
    // `data` is optional-chained too: a proxy-generated 403/429 has no JSON
    // body at all, and reading `.message` off `undefined` threw INSIDE this
    // interceptor, replacing the real error with a TypeError and skipping the
    // code-preserving reject below.
    const message = error.response?.data?.message as ErrorMessage;
    const description = translateError(message);
    const errorBody = error.response?.data as
      | { code?: string; feature?: string; currentTier?: string }
      | undefined;

    // Auth screens render their OWN inline error banner inside the form
    // (form.setError("root", ...) in register/login pages). Showing a
    // global toast on top of that gives the user two popups for the
    // same error and confuses the read. Suppress the toast for auth
    // endpoints — auth pages handle their own error UX.
    const requestUrl: string = typeof error.config?.url === "string" ? error.config.url : "";
    const isAuthEndpoint = requestUrl.startsWith("/auth/") || requestUrl === "/auth";

    if (!isAuthEndpoint) {
      // A paywall rejection used to render NOTHING: `translateError` switches
      // over the ErrorMessage enum, which holds no paywall codes, so
      // `description` was null; the status is 403 so the 422 branch was
      // skipped; no toast was emitted. Every paywall hit in the product was a
      // silent dead click. Surface an upgrade CTA instead — and return early,
      // so the user never gets a bare red "Oops" on top of it.
      if (isPaywallCode(errorBody?.code) && error.config?.suppressPaywall) {
        // The caller owns the upgrade surface for this request.
      } else if (isPaywallCode(errorBody?.code)) {
        openPaywall({
          code: errorBody.code,
          feature: errorBody.feature,
          currentTier: errorBody.currentTier,
        });
      } else if (error.config?.suppressErrorToast) {
        // The caller renders this failure inline.
      } else if (description) {
        toast({
          variant: "error",
          title: t`Oops, the server returned an error.`,
          description,
        });
      } else if (error.response?.status === 422) {
        // Zod validation error from the server
        const errors = error.response?.data?.errors;
        const validationMessage =
          Array.isArray(errors) && errors.length > 0
            ? errors.map((e: { message?: string }) => e.message).join(", ")
            : t`The data you provided is invalid. Please check your input and try again.`;

        toast({
          variant: "error",
          title: t`Oops, the server returned an error.`,
          description: validationMessage,
        });
      }
    }

    // Preserve the server's structured error `code` (e.g.
    // AI_TAILORING_DAILY_LIMIT_REACHED) so feature code can branch on it
    // instead of string-matching the human message.
    //
    // `response`/`config`/`request` MUST be carried over onto the replacement
    // error. Response interceptors run in registration order, so this one fires
    // BEFORE the axios-auth-refresh interceptor registered at the bottom of this
    // file — and that library's `shouldInterceptError` bails out unless it can
    // read `error.response.status` (and it retries via `error.response.config`).
    // Rejecting with a bare `new Error` silently disabled token refresh
    // entirely: every user was logged out the moment their 15-minute access
    // token expired, despite holding a 30-day Refresh cookie.
    return Promise.reject(
      Object.assign(new Error(typeof message === "string" ? message : "ValidationError"), {
        code: errorBody?.code,
        isAxiosError: true,
        config: error.config,
        request: error.request,
        response: error.response,
      }),
    );
  },
);

/** Structured error code attached by the response interceptor above. */
export const errorCode = (error: unknown): string | undefined =>
  (error as { code?: string } | null)?.code;

// Create another instance to handle failed refresh tokens
// Reference: https://github.com/Flyrell/axios-auth-refresh/issues/191
const axiosForRefresh = _axios.create({ baseURL: "/api", withCredentials: true });

/**
 * Backoff delays (ms) between retries of a TRANSIENT `/auth/refresh` failure
 * (a 502 during a server restart/deploy, a dropped connection, a timeout).
 *
 * Regression (2026-09-03): a deploy or a brief upstream stall makes
 * `/auth/refresh` 502 for a handful of seconds. Without ANY retry, the very
 * first failed refresh nulled the cached user and bounced a still-valid
 * session to /auth/login.
 *
 * Kept intentionally SHORT (Codex review on PR #577, DO NOT MERGE #1): every
 * OTHER request queued behind axios-auth-refresh's shared refresh promise
 * blocks for as long as this loop runs, so widening it (the original fix
 * used 4 delays, ~15s) turned a brief blip into a ~15s stall for every
 * in-flight request on the page. This budget only needs to ride out a
 * genuine short blip — 3 calls across ~3s. A longer outage is NOT this
 * loop's job: AuthGuard owns the long-lived retry story with its own
 * growing-backoff reconnect poll (`RECONNECT_POLL_SCHEDULE_MS` in
 * `router/guards/auth.tsx`), which keeps re-probing for minutes without
 * blocking anything.
 */
export const AUTH_REFRESH_RETRY_DELAYS_MS = [1000, 2000];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Interceptor to handle expired access token errors.
//
// On a genuinely dead session — a 401/403 from the server, meaning a
// logged-out visitor or a Refresh cookie past its 30 days — drop the cached
// user and rethrow immediately, so the original request rejects and
// AuthGuard can send them to /auth/login.
//
// On a TRANSIENT failure (5xx, a network error with no response, a
// timeout — none of which say anything about the session itself) retry
// with backoff instead. Only once the retry budget is exhausted do we give
// up — and even then we do NOT null the cached user, since we still don't
// know whether the session is actually dead.
//
// `invalidateQueries` MUST NOT be used here. It refetches /user/me, which
// 401s, which re-enters this very interceptor and awaits the same cached
// refresh promise that is waiting on us: a circular wait that left the query
// pending forever, so `loading` never cleared and authed routes hung on a
// blank screen. `setQueryData` updates the cache without issuing a request.
const handleAuthError = async () => {
  // `attempt` indexes AUTH_REFRESH_RETRY_DELAYS_MS: the very first call is
  // unconditional, then one more call follows each backoff delay. Once the
  // array is exhausted (3 total calls, ~3s) a transient failure is rethrown
  // as-is.
  //
  // By design (Codex review on PR #577, DO NOT MERGE #7): every OTHER
  // request queued behind this shared refresh promise (axios-auth-refresh's
  // retry mechanism) waits AT MOST this ~3s budget before either resuming
  // (refresh recovered) or failing alongside this one (budget exhausted).
  // That's a deliberate ceiling — see AUTH_REFRESH_RETRY_DELAYS_MS above for
  // why this loop stays short instead of trying to outlast a whole outage.
  let attempt = 0;

  for (;;) {
    try {
      return await refreshToken(axiosForRefresh);
    } catch (error) {
      if (isDefinitiveAuthFailure(error)) {
        queryClient.setQueryData(USER_KEY, null);
        throw error;
      }

      // Explicit bounds check (not `.at()`): the runtime exhaustion
      // condition must not depend on a tsconfig setting
      // (`noUncheckedIndexedAccess`, which this repo doesn't enable) to be
      // meaningful.
      if (attempt >= AUTH_REFRESH_RETRY_DELAYS_MS.length) throw error;
      const delay = AUTH_REFRESH_RETRY_DELAYS_MS[attempt];

      await sleep(delay);
      attempt += 1;
    }
  }
};

// Refresh the access token on 401, and on a 403 that carries no structured
// `code`.
//
// 403 must stay in the list: some genuine auth failures surface as a bare
// ForbiddenException. But EVERY paywall throw is also a 403, and routing those
// through the refresh path cost three round-trips, rotated the refresh token,
// and — if that refresh failed — ran `handleAuthError`, which nulls the cached
// user. A user hitting a paywall could be signed OUT instead of shown an
// upgrade prompt. Structured paywall bodies always carry `code`, so skipping
// them is precise. (`shouldRefresh` supersedes `statusCodes` entirely.)
createAuthRefreshInterceptor(axios, handleAuthError, {
  shouldRefresh: (error) => {
    const status = error.response?.status;
    if (status === 401) return true;
    if (status !== 403) return false;
    return !(error.response?.data as { code?: string } | undefined)?.code;
  },
});
